import { h, type Ref } from 'vue'
import { getAlgoliaResults } from '@algolia/autocomplete-js'
import { Map, MapPin } from 'lucide-vue-next'
import {
  getLocationsIndexName,
  getCategoriesIndexName,
  getFederatedSearchIndexName,
} from '~/utils/algoliaHelpers'
import { sanitizeAlgoliaQueryId } from '~/utils/helpers'
import {
  getProductPath,
  mountUrlParamsFromItem,
  getLocationDisplayName,
  getLocationNameAttributeForAlgolia,
  GEO_PRECISION,
  HITS_PER_PAGE,
} from './helpers'

interface SourceDependencies {
  searchClient: any
  locale: Ref<string>
  isMobile: boolean
  localePath: (path: string) => string
  t: (key: string) => string
  router: any
  eventsQueryId: Ref<string | null>
}

export const useFederatedSearchSources = (deps: SourceDependencies) => {
  const {
    searchClient,
    locale,
    isMobile,
    localePath,
    t,
    router,
    eventsQueryId,
  } = deps

  const createLocalitiesSource = () => ({
    sourceId: 'locations',
    getItemInputValue: ({ item }: any) =>
      getLocationDisplayName(item, locale.value),
    getItems({ query }: any) {
      const nameAttr = getLocationNameAttributeForAlgolia(locale.value)
      return getAlgoliaResults({
        searchClient,
        queries: [
          {
            indexName: getLocationsIndexName(),
            query,
            params: {
              hitsPerPage: isMobile
                ? HITS_PER_PAGE.locations.mobile
                : HITS_PER_PAGE.locations.desktop,
              attributesToSnippet: [`${nameAttr}:24`],
              snippetEllipsisText: '…',
              optionalWords: query,
              aroundLatLngViaIP: true,
              aroundRadius: 'all',
              aroundPrecision: GEO_PRECISION,
            },
          },
        ],
        transformResponse({ hits }: any) {
          return hits.map((indexHits: any[]) => indexHits)
        },
      })
    },
    transformResponse({ hits }: any) {
      return hits.map((indexHits: any[]) =>
        indexHits.filter((h) => h.type !== 'admin_area')
      )
    },
    getItemUrl({ item }: any) {
      return localePath(`/search/?${mountUrlParamsFromItem(item)}`)
    },
    templates: {
      header() {
        return h('div', { class: 'mr-8' }, [
          h(
            'span',
            { class: 'aa-SourceHeaderTitle' },
            t('home.header.search.selectCity')
          ),
          h('div', { class: 'aa-SourceHeaderLine' }),
        ])
      },
      item({ item, components }: any) {
        const nameAttr = getLocationNameAttributeForAlgolia(locale.value)
        const LocationIcon = item?.type === 'region' ? Map : MapPin
        return h('div', { class: 'aa-ItemContent items-center gap-2' }, [
          h(LocationIcon, {
            class: 'shrink-0 text-stone-600',
            size: 20,
            'aria-hidden': true,
          }),
          h('span', { class: 'aa-ItemContentTitle' }, [
            item?._highlightResult?.[nameAttr]
              ? components.Highlight({ hit: item, attribute: nameAttr })
              : getLocationDisplayName(item, locale.value),
          ]),
        ])
      },
    },
    onSelect({ item, setIsOpen }: any) {
      setIsOpen(false)
      router.push(localePath(`/search/?${mountUrlParamsFromItem(item)}`))
    },
  })

  const createCategoriesSource = () => ({
    sourceId: 'eventCategories',
    getItemInputValue: ({ item }: any) => item.query,
    getItems({ query }: any) {
      return getAlgoliaResults({
        searchClient,
        queries: [
          {
            indexName: getCategoriesIndexName(locale.value),
            query,
            params: {
              hitsPerPage: HITS_PER_PAGE.categories,
              attributesToSnippet: ['name:24'],
              snippetEllipsisText: '…',
              optionalWords: query,
            },
          },
        ],
      })
    },
    getItemUrl({ item }: any) {
      return localePath(`/c/${item?.slug}`)
    },
    templates: {
      header() {
        return h('div', null, [
          h('span', { class: 'aa-SourceHeaderTitle' }, t('general.categories')),
          h('div', { class: 'aa-SourceHeaderLine' }),
        ])
      },
      item({ item, components }: any) {
        return h('div', { class: 'aa-ItemContent' }, [
          h('span', { class: 'aa-ItemContentTitle' }, item.name),
          components.Highlight({ hit: item, attribute: 'name' }),
        ])
      },
    },
    onSelect({ item, setIsOpen }: any) {
      setIsOpen(false)
      router.push(localePath(`/c/${item.slug}`))
    },
  })

  const createEventsSource = (setContext: any) => ({
    sourceId: 'events',
    getItemInputValue: ({ item }: any) => item.query,
    getItems({ query }: any) {
      return getAlgoliaResults({
        searchClient,
        queries: [
          {
            indexName: getFederatedSearchIndexName(locale.value),
            query,
            params: {
              hitsPerPage: HITS_PER_PAGE.events,
              attributesToSnippet: ['name:24'],
              snippetEllipsisText: '…',
              aroundLatLngViaIP: true,
              aroundRadius: 'all',
              aroundPrecision: GEO_PRECISION,
            },
          },
        ],
        transformResponse({ results, hits }: any) {
          eventsQueryId.value = results?.[0]?.queryID ?? null
          setContext({
            redirects: results?.[0]?.renderingContent?.redirect,
          })
          return hits
        },
      })
    },
    getItemUrl({ item }: any) {
      return item.url + ' '
    },
    templates: {
      header() {
        return h('div', null, [
          h('span', { class: 'aa-SourceHeaderTitle' }, t('general.events')),
          h('div', { class: 'aa-SourceHeaderLine' }),
        ])
      },
      item({ item }: any) {
        return h('div', { class: 'aa-ItemContent' }, [
          h('img', {
            class: 'w-20 h-16 object-cover mr-2 rounded-md',
            src: item.main_image?.thumb,
            alt: item.main_image?.alt,
          }),
          h('span', { class: 'aa-ItemContentTitle' }, item.title),
        ])
      },
      noResults() {
        return t('general.select.noResultsFound')
      },
    },
    onSelect({ item, setIsOpen }: any) {
      setIsOpen(false)
      const path = localePath(getProductPath(item))
      const qid = sanitizeAlgoliaQueryId(eventsQueryId.value)
      const url = qid ? `${path}?qid=${qid}` : path
      window.location.href = url
    },
  })

  return ({ setContext }: any) => [
    createLocalitiesSource(),
    createCategoriesSource(),
    createEventsSource(setContext),
  ]
}
